import { useGuest } from '../services/GuestContext';
import AdminUserProfile from '../components/AdminUserProfile';
import UserManagementConfig from '../components/UserManagementConfig';
import AccountDeletion from '../components/AccountDeletion';
import InvitationPreview from '../components/InvitationPreview';
import {useFarm} from '../services/farmContext';
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Card, Badge } from "../components/ui";
import { useAuth } from "../services/authContext";
import { MENU_PAGES } from "../services/auth";
import { Role, UserStatus, UserAccount, PrivilegeMap, FeaturePrivilegeKey } from "../types";
import { Plus, Search, UserRound, ShieldCheck, X } from "lucide-react";
import { useSettingsChanges } from "../services/settingsChanges";
import {
  accessLevel,
  privilegeForAccess,
  AccessLevel,
} from "../services/adminPermissions";
const roleOptions: Role[] = ["Admin", "Manager", "Worker", "Hunter", "Family", "Friends", "VIP", "Guest"];
const roleHelp: Record<Role, string> = {
  Family: "Family community and eligible events.",
  Friends: "Friends community and eligible events.",
  Worker: "Project participation and work logging.",
  Admin: "Full administration access. Permissions are fixed.",
  Manager: "Operational role. Review its configured permissions below.",
  Hunter: "Field role. Review its configured permissions below.",
  VIP: "Custom access for trusted visitors.",
  Guest: "Limited access for invited visitors.",
};
export default function UserManagement() {
  const {activeFarmId}=useFarm();
  const [inviteWelcomeFarm,setInviteWelcomeFarm]=useState(''),[draftWelcomeFarm,setDraftWelcomeFarm]=useState('');
  const {
    users,
    roles,
    user: currentUser,
    invite,
    persistUser,
    deleteUser,
    refreshUsers,
    saveRolePrivileges,
    loading,
    mutating,
    error,
  } = useAuth();
  const {farms} = useGuest();
  const [draftRoles,setDraftRoles]=useState<Role[]>([]),[inviteRoles,setInviteRoles]=useState<Role[]>(['Friends']);
  const [draftFarms,setDraftFarms]=useState<string[]>([]),[inviteFarms,setInviteFarms]=useState<string[]>([]);
  const [draftStatus,setDraftStatus]=useState<UserStatus>('Active');
  const rolePicker=(values:Role[],change:(roles:Role[])=>void,disabled:boolean,lockAdmin=false)=> <fieldset className="space-y-2"><legend className="text-sm font-medium mb-2">Roles (choose all that apply)</legend><div className="grid grid-cols-2 gap-2">{roleOptions.filter(r=>!['VIP','Guest'].includes(r)||values.includes(r)).map(r=><label key={r} className="flex gap-2 items-center text-sm rounded-lg border p-3"><input type="checkbox" disabled={disabled||(lockAdmin&&r==='Admin')} checked={values.includes(r)} onChange={e=>change(e.target.checked?[...values,r]:values.filter(v=>v!==r))}/>{r}{['VIP','Guest'].includes(r)?' (legacy)':''}</label>)}</div></fieldset>;
  const farmPicker=(values:string[],change:(ids:string[])=>void,disabled:boolean)=><fieldset className="space-y-2"><legend className="text-sm font-medium mb-2">Property access</legend>{farms.map(f=><label key={f.farm_id} className="flex items-center gap-2 text-sm"><input type="checkbox" disabled={disabled} checked={values.includes(f.farm_id)} onChange={e=>change(e.target.checked?[...values,f.farm_id]:values.filter(id=>id!==f.farm_id))}/>{f.name}</label>)}{!farms.length&&<p className="text-sm text-gray-500">Create a property before inviting guests.</p>}</fieldset>;
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "config" ? "config" : params.get("tab") === "roles" ? "roles" : "people";
  const [query, setQuery] = useState(""),
    [roleFilter, setRoleFilter] = useState(""),
    [selected, setSelected] = useState<UserAccount | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false),
    [email, setEmail] = useState(""),
    [lastInvite, setLastInvite] = useState("");
  const [role, setRole] = useState<Role>("Hunter"),
    [permissions, setPermissions] = useState<PrivilegeMap | null>(null),
    [baseline, setBaseline] = useState("");
  const [busy, setBusy] = useState(false),
    [localError, setError] = useState(""),
    [message, setMessage] = useState(""),
    [confirmRemove, setConfirmRemove] = useState(false);
  const [visitDirty,setVisitDirty]=useState(false);
  const [invitationDirty,setInvitationDirty]=useState(false);
  const roleDefinition = roles.find((r) => r.role === role);
  const roleDirty = !!permissions && JSON.stringify(permissions) !== baseline;
  const accessDirty = !!selected && (JSON.stringify(draftRoles)!==JSON.stringify(selected.roles||[selected.role]) || JSON.stringify(draftFarms)!==JSON.stringify(selected.farm_ids||[]) || draftStatus!==selected.status || draftWelcomeFarm!==(selected.onboarding_farm_id||''));
  useSettingsChanges(
    invitationDirty || visitDirty || roleDirty ||
      (accessDirty) ||
      (inviteOpen && !!email.trim()),
  );
  const userDirty = accessDirty;
  const isBusy = busy || mutating || loading;
  useEffect(() => {
    if (roleDefinition && !roleDirty) {
      setPermissions(structuredClone(roleDefinition.privileges));
      setBaseline(JSON.stringify(roleDefinition.privileges));
    }
  }, [roleDefinition, roleDirty]);
  const discard = () =>
    !(invitationDirty || roleDirty || userDirty || visitDirty) ||
    window.confirm("Discard unsaved changes?");
  const chooseUser = (u: UserAccount) => {
    if (!discard()) return;
    setSelected(u);
    setLastInvite('');setDraftWelcomeFarm(u.onboarding_farm_id||'');
    setDraftRoles(u.roles||[u.role]);setDraftFarms(u.farm_ids||[]);setDraftStatus(u.status);
    setConfirmRemove(false);
    setInviteOpen(false);
    setError("");
    setMessage("");
  };
  const changeRole = (value: Role) => {
    if (!discard()) return;
    setRole(value);
    const p = roles.find((r) => r.role === value)?.privileges;
    setPermissions(p ? structuredClone(p) : null);
    setBaseline(JSON.stringify(p));
    setMessage("");
    setError("");
  };
  const run = async (action: () => Promise<void>, success: string) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await action();
      setMessage(success);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Could not save changes. Try again.",
      );
    } finally {
      setBusy(false);
    }
  };
  const addUser = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalized = email.trim().toLowerCase();
    if (users.some((u) => u.email.toLowerCase() === normalized)) {
      setError("This user already exists. Select them to change their role.");
      return;
    }
    await run(async () => {
      if(!inviteRoles.length)throw new Error('Select at least one role.');
      const welcomeFarm=inviteWelcomeFarm||((inviteRoles.includes('Admin')||inviteFarms.includes(activeFarmId||''))?activeFarmId:null)||(inviteFarms.length===1?inviteFarms[0]:null);
      if(!welcomeFarm)throw new Error('Choose a welcome property.');
      await persistUser({email:normalized,name:normalized.split('@')[0],role:inviteRoles[0],roles:inviteRoles,farm_ids:inviteFarms,onboarding_farm_id:welcomeFarm,status:'Invited',server_version:0,create_only:true});
      setLastInvite(normalized);
      setInviteOpen(false);
      setEmail("");
    }, "User added. They can sign in with this Google account. No email has been sent.");
  };
  const saveUser = () =>
    selected &&
    run(async () => {
      if(!draftRoles.length)throw new Error('Select at least one role.');
      const updated={...selected,role:draftRoles.includes('Admin')?'Admin' as Role:draftRoles[0],roles:draftRoles,farm_ids:draftFarms,onboarding_farm_id:draftWelcomeFarm||null,status:draftStatus};
      await persistUser(updated);setSelected(updated);
    }, "User access saved.");
  const savePermissions = () =>
    permissions &&
    run(async () => {
      await saveRolePrivileges(role, permissions);
      setBaseline(JSON.stringify(permissions));
    }, `${role} permissions saved.`);
  const visible = users
    .filter(
      (u) =>
        (!roleFilter || (u.roles||[u.role]).includes(roleFilter as Role)) &&
        `${u.name} ${u.email}`.toLowerCase().includes(query.toLowerCase()),
    )
    .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
  const protectedUser =
    selected?.email === currentUser?.email;
  const permissionRows = [
    ...MENU_PAGES.filter(
      (p) => p.key !== "overview" && p.key !== "settings",
    ).map((p) => ({
      key: p.key as FeaturePrivilegeKey,
      label:
        p.key === "map"
          ? "Field Map"
          : p.key === "scouting"
            ? "Work photos"
            : p.label,
    })),
    { key: "gpsTracking" as FeaturePrivilegeKey, label: "Routes & location" },
    {key:"contributionReview" as FeaturePrivilegeKey,label:"Contribution review"},
    {key:'communityParticipation' as FeaturePrivilegeKey,label:'Nature Gallery participation'},
    {key:'photoModeration' as FeaturePrivilegeKey,label:'Photo & comment moderation'},
    {key:'neighborContacts' as FeaturePrivilegeKey,label:'Neighbor contacts & notes'},
  ];
  return (
    <section className="space-y-5">
      <header>
        <h2 className="text-xl font-semibold">Users & access</h2>
        <p className="text-sm text-gray-500 mt-1">
          Google accounts and roles are shared across the workspace.
        </p>
      </header>
      <nav aria-label="User settings" className="flex flex-wrap gap-2 border-b pb-3">
        {[
          ["people", "People"],
          ["roles", "Role permissions"],
          ["config", "Config"],
        ].map(([id, label]) => (
          <button
            key={id}
            disabled={isBusy}
            aria-pressed={tab === id}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${tab === id ? "bg-usda-100 text-usda-900" : "text-gray-600"}`}
            onClick={() => {
              if (discard()) {
                setSelected(null);
                setInviteOpen(false);
                setPermissions(
                  roleDefinition
                    ? structuredClone(roleDefinition.privileges)
                    : null,
                );
                setBaseline(JSON.stringify(roleDefinition?.privileges));
                setParams(id === "people" ? {} : {tab:id});
                setError("");
                setMessage("");
              }
            }}
          >
            {label}
          </button>
        ))}
      </nav>
      {(localError || error) && (
        <p
          role="alert"
          className="p-3 rounded-lg bg-red-50 text-red-700 text-sm"
        >
          {localError || error}
        </p>
      )}
      {message && (
        <div
          role="status"
          className="text-sm text-usda-700 bg-usda-50 rounded-lg p-3"
        >
          {message}
        </div>
      )}
      {lastInvite&&<InvitationPreview key={lastInvite} email={lastInvite} onClose={()=>setLastInvite('')} onDirtyChange={setInvitationDirty}/>}
      {tab === "config" ? <UserManagementConfig key={currentUser?.email||'signed-out'} onBusyChange={setBusy}/> : tab === "people" ? (
        <>
          <div
            className={`flex-wrap items-center gap-3 ${selected || inviteOpen ? "hidden xl:flex" : "flex"}`}
          >
            <label className="flex gap-2 items-center border rounded-lg bg-white p-3 flex-1 min-w-40">
              <Search size={18} className="text-gray-400" />
              <input
                aria-label="Search users"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name or email…"
                className="w-full outline-none text-sm"
              />
            </label>
            <select
              aria-label="Filter users by role"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="border rounded-lg p-3 text-sm"
            >
              <option value="">All roles</option>
              {roleOptions.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
            <Button
              disabled={isBusy}
              onClick={() => {
                if (discard()) {
                  setSelected(null);
                  setInviteOpen(true);setInviteFarms(farms.map(f=>f.farm_id));
                  setMessage("");
                  setError("");
                  setLastInvite("");
                }
              }}
            >
              <Plus size={16} className="mr-2" />
              Add user
            </Button>
          </div>
          {(selected || inviteOpen) && (
            <button
              className="xl:hidden text-sm font-medium text-usda-700"
              disabled={isBusy}
              onClick={() => {
                if (discard()) {
                  setSelected(null);
                  setInviteOpen(false);
                }
              }}
            >
              ← Back to people
            </button>
          )}
          <div className="grid xl:grid-cols-2 gap-5 items-start">
            <Card
              className={`overflow-hidden ${selected || inviteOpen ? "hidden xl:block" : " "}`}
            >
              <div className="px-4 py-3 border-b text-xs text-gray-500">
                {visible.length} {visible.length === 1 ? "person" : "people"}
              </div>
              {loading ? (
                <p role="status" className="p-5 text-sm text-gray-500">
                  Loading people…
                </p>
              ) : (
                visible.map((u) => (
                  <button
                    key={u.email}
                    disabled={isBusy}
                    onClick={() => chooseUser(u)}
                    className={`p-4 w-full text-left border-b last:border-0 flex gap-3 items-center hover:bg-gray-50 ${selected?.email === u.email ? "bg-usda-50" : ""}`}
                  >
                    <span className="h-10 w-10 bg-usda-100 rounded-full flex items-center justify-center text-usda-700 font-semibold shrink-0">
                      {(u.name || u.email).slice(0, 1).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-sm truncate">
                        {u.name || u.email}
                        {u.email === currentUser?.email ? " (you)" : ""}
                      </span>
                      <span className="block text-xs text-gray-500 break-all mt-1">
                        {u.email}
                      </span>
                    </span>
                    <span className="text-right space-y-1 shrink-0">
                      <span className="block text-xs font-medium">
                        {(u.roles||[u.role]).join(", ") || "No roles"}
                      </span>
                      {u.status !== "Active" && (
                        <Badge variant="warning">{u.status}</Badge>
                      )}
                    </span>
                  </button>
                ))
              )}
              {!loading && !visible.length && (
                <p className="p-5 text-sm text-gray-500">
                  No people match your search.
                </p>
              )}
            </Card>
            {inviteOpen ? (
              <Card className="p-5">
                <form onSubmit={addUser} className="space-y-5">
                  <h3 className="font-semibold">Add a Google account</h3>
                  <p className="text-sm text-gray-500">
                    Create an invitation here, then share the app link. Their
                    first sign-in activates the account.
                  </p>
                  <label className="block text-sm font-medium">
                    Google account email
                    <input
                      type="email"
                      required
                      value={email}
                      disabled={isBusy}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full border rounded-lg p-3 mt-1.5"
                      placeholder="name@gmail.com"
                    />
                  </label>
                  {rolePicker(inviteRoles,setInviteRoles,isBusy)}
                  {farmPicker(inviteFarms,setInviteFarms,isBusy)}
                  <label className="block text-sm font-medium">Welcome property<select required disabled={isBusy} value={inviteWelcomeFarm||((inviteRoles.includes('Admin')||inviteFarms.includes(activeFarmId||''))?activeFarmId||'':'')||(inviteFarms.length===1?inviteFarms[0]:'')} onChange={event=>setInviteWelcomeFarm(event.target.value)} className="block w-full border rounded-lg p-3 mt-2"><option value="">Choose a property</option>{farms.filter(f=>inviteRoles.includes('Admin')||inviteFarms.includes(f.farm_id)).map(f=><option key={f.farm_id} value={f.farm_id}>{f.name}</option>)}</select></label>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isBusy}
                      onClick={() => setInviteOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isBusy || !email.trim()}>
                      {isBusy ? "Adding…" : "Add user"}
                    </Button>
                  </div>
                </form>
              </Card>
            ) : selected ? (
              <Card className="p-5 space-y-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold">
                      {selected.name || selected.email}
                    </h3>
                    <p className="text-sm text-gray-500 break-all mt-1">
                      {selected.email}
                    </p>
                  </div>
                  <button
                    aria-label="Close user details"
                    disabled={isBusy}
                    onClick={() => {
                      if (discard()) setSelected(null);
                    }}
                  >
                    <X size={18} />
                  </button>
                </div>
                <Badge
                  variant={selected.status === "Active" ? "success" : "warning"}
                >
                  {selected.status}
                </Badge>
                {selected.status === "Invited" && (
                  <p className="text-sm text-gray-500">
                    Waiting for their first Google sign-in.
                  </p>
                )}
                {selected.status==='Invited'&&<Button variant="secondary" disabled={isBusy||userDirty} onClick={()=>setLastInvite(selected.email)}>Generate invitation email</Button>}
                {rolePicker(draftRoles,setDraftRoles,isBusy,protectedUser)}
                {farmPicker(draftFarms,setDraftFarms,isBusy||protectedUser)}
                <label className="block text-sm font-medium">Welcome property<select disabled={isBusy} value={draftWelcomeFarm} onChange={event=>setDraftWelcomeFarm(event.target.value)} className="block w-full border rounded-lg p-3 mt-2"><option value="">Choose a property</option>{farms.filter(f=>draftRoles.includes('Admin')||draftFarms.includes(f.farm_id)).map(f=><option key={f.farm_id} value={f.farm_id}>{f.name}</option>)}</select></label>
                <label className="block text-sm font-medium">Account status<select className="block border rounded-lg p-3 mt-2 w-full" disabled={isBusy||protectedUser} value={draftStatus} onChange={e=>setDraftStatus(e.target.value as UserStatus)}>{['Active','Invited','Pending','Suspended'].map(status=><option key={status}>{status}</option>)}</select></label>
                {selected.status==='Pending'&&<p className="text-sm text-gray-500">To approve this account, select roles, property access, and Active status.</p>}
                {protectedUser&&<p className="text-sm text-gray-500">You can change your additional roles. Your Admin role and Active status are protected.</p>}
                <div className="flex items-center justify-end border-t pt-4">
                  <Button
                    disabled={isBusy || !userDirty}
                    onClick={saveUser}
                  >
                    {isBusy ? "Saving…" : "Save access"}
                  </Button>
                </div>
                <AdminUserProfile key={selected.email} account={selected} disabled={isBusy} onBusyChange={setBusy} onDraftChange={setVisitDirty}/>
                {!protectedUser && (
                  <div className="border-t pt-4 space-y-4">
                    {confirmRemove ? (
                      <div className="space-y-3">
                        <p className="text-sm text-gray-600">
                          Suspend {selected.email}? This
                          keeps their photos and work history.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            disabled={isBusy}
                            onClick={() => setConfirmRemove(false)}
                          >
                            Keep user
                          </Button>
                          <Button
                            variant="danger"
                            disabled={isBusy}
                            onClick={() =>
                              run(async () => {
                                await deleteUser(selected.email);
                                setSelected(null);
                                setConfirmRemove(false);
                              }, "User suspended.")
                            }
                          >
                            Suspend user
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <button
                        disabled={isBusy}
                        onClick={() => setConfirmRemove(true)}
                        className="text-sm text-red-700"
                      >
                        Suspend user
                      </button>
                    )}
                    {userDirty&&<p className="text-sm text-gray-500">Save or discard access changes before deleting this account.</p>}
                    <AccountDeletion key={selected.email} email={selected.email} disabled={isBusy||userDirty||visitDirty} onBusyChange={setBusy} onDeleted={async()=>{setSelected(null);setConfirmRemove(false);setLastInvite('');setMessage('Account permanently deleted.');await refreshUsers();}}/>
                  </div>
                )}
              </Card>
            ) : (
              <div className="p-6 text-sm text-gray-500">
                <UserRound size={24} className="mb-3 text-gray-400" />
                Select a person to review their account and role.
              </div>
            )}
          </div>
        </>
      ) : (
        <Card className="p-5 sm:p-6 space-y-5">
          <div className="flex items-start gap-3">
            <ShieldCheck size={24} className="text-usda-600 shrink-0" />
            <div>
              <h3 className="font-semibold">Permissions by role</h3>
              <p className="text-sm text-gray-500 mt-1">
                Changes apply to everyone assigned to this role. Admin settings
                are always restricted to administrators.
              </p>
            </div>
          </div>
          <label className="block text-sm font-medium">
            Role
            <select
              value={role}
              disabled={isBusy}
              onChange={(e) => changeRole(e.target.value as Role)}
              className="block border rounded-lg p-3 mt-1.5 w-full sm:w-64"
            >
              {roleOptions.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </label>
          <p className="text-sm text-gray-500">{roleHelp[role]}</p>
          {permissions ? (
            <div className="divide-y border rounded-xl">
              {permissionRows.map(({ key, label }) => (
                <label
                  key={key}
                  className="flex flex-wrap items-center justify-between gap-3 p-4 text-sm font-medium"
                >
                  {label}
                  <select
                    aria-label={`${label} access`}
                    value={
                      accessLevel(permissions[key])
                    }
                    disabled={isBusy || role === "Admin"}
                    onChange={(e) => {
                      setPermissions({
                        ...permissions,
                        [key]: privilegeForAccess(
                          e.target.value as AccessLevel,
                        ),
                      });
                      setMessage("");
                    }}
                    className="max-w-full rounded-lg border px-3 py-2 font-normal"
                  >
                    <option value="none">
                      No access
                    </option>
                    <option value="read">{key === "gpsTracking" ? "View routes & use location" : "View only"}</option>
                    <option value="write">
                      {key === "gpsTracking" ? "Record routes & share location" : "View & edit"}
                    </option>
                  </select>
                </label>
              ))}
              <div className="p-4 text-sm flex flex-wrap items-start justify-between gap-3">
                <div><p className="font-medium">Patches</p><p className="text-xs text-gray-500 mt-1">Available to every active property member. Administrators manage patch definitions.</p></div>
                <span className="text-xs text-usda-700 bg-usda-50 rounded-lg px-3 py-2">All active members</span>
              </div>
            </div>
          ) : (
            <p role="status" className="text-sm text-gray-500">
              Loading role permissions…
            </p>
          )}
          <div className="flex items-center justify-between gap-3 border-t pt-4">
            <p className="text-sm text-gray-500">
              {roleDirty ? "Unsaved changes" : ""}
            </p>
            <Button
              disabled={isBusy || !roleDirty || role === "Admin"}
              onClick={savePermissions}
            >
              {isBusy ? "Saving…" : "Save permissions"}
            </Button>
          </div>
        </Card>
      )}
    </section>
  );
}
